import React from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, Stack, Switch, TextField } from '@mui/material';
import ConfigCard from './ConfigCard';
import { PdiRow } from './_PdiHelpers';

/**
 * Variant grouping — sister card of ProductGridCard. The two indented
 * controls only matter when the parent toggle is on, so we render them
 * with `indent` to make that nesting visually obvious.
 */
const VariantGroupingCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    const groupAsOne   = !!get('groupAsOne', true);
    const showSwatches = !!get('showSwatches', true);
    const maxVariants  = get('maxVariants', 4);

    return (
        <ConfigCard
            anchor="cfg-card-grid-variants"
            title={tr('cfg_grid_variants_title', 'Variant grouping')}
            description={tr('cfg_grid_variants_desc', 'How product variants (different colors of the same item) appear in the grid.')}
            theme={theme}
        >
            <PdiRow
                label={tr('cfg_grid_group_as_one_label', 'Group variants as one product')}
                help={tr('cfg_grid_group_as_one_help', 'When on, all color variants of an item share a single card. When off, each variant gets its own card in the grid.')}
                control={
                    <Switch
                        color="success"
                        size="small"
                        checked={groupAsOne}
                        onChange={(e) => set('groupAsOne')(e.target.checked)}
                    />
                }
            />

            <Stack spacing={1} sx={{ pl: 0 }}>
                <PdiRow
                    indent
                    label={tr('cfg_grid_show_swatches_label', 'Show variant swatches on the card')}
                    help={tr('cfg_grid_show_swatches_help', 'Display small color dots on each product card showing the available variants')}
                    control={
                        <Switch
                            color="success"
                            size="small"
                            checked={showSwatches}
                            disabled={!groupAsOne}
                            onChange={(e) => set('showSwatches')(e.target.checked)}
                        />
                    }
                />
                <PdiRow
                    indent
                    label={tr('cfg_grid_max_variants_label', 'Maximum variants shown on a card')}
                    help={tr('cfg_grid_max_variants_help', 'If a product has more variants than this, the rest are summarized as "+N"')}
                    control={
                        <TextField
                            size="small"
                            type="number"
                            value={maxVariants}
                            disabled={!groupAsOne || !showSwatches}
                            onChange={(e) => {
                                const n = e.target.value === '' ? '' : Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 0));
                                set('maxVariants')(n);
                            }}
                            inputProps={{ min: 1, max: 99, style: { width: 56, textAlign: 'right' } }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {tr('cfg_grid_max_variants_suffix', 'variants')}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    }
                />
            </Stack>
        </ConfigCard>
    );
};

VariantGroupingCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default VariantGroupingCard;
